import { faPlus, faTrash, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo } from "react";

import { useLocale } from "../stores/locale";
import { Button } from "./button";
import { Input } from "./input";

export type PropertyPartFormRow = {
  id?: string;
  code: string;
  name: string;
};

export type PropertyFormValues = {
  name: string;
  parts: PropertyPartFormRow[];
};

function createEmptyPart(): PropertyPartFormRow {
  return { code: "", name: "" };
}

export function PropertyForm({
  onCancel,
  onChange,
  onSubmit,
  values,
  loading = false,
  disabled = false,
  hasError = false,
}: {
  onCancel: () => void;
  onChange: (values: PropertyFormValues) => void;
  onSubmit: () => void;
  values: PropertyFormValues;
  loading?: boolean;
  disabled?: boolean;
  hasError?: boolean;
}) {
  const t = useLocale((state) => state.locale);
  const isValid = useMemo(
    () =>
      values.name.trim() !== "" &&
      values.parts.every(
        (part) => part.code.trim() !== "" && part.name.trim() !== "",
      ),
    [values.name, values.parts],
  );
  const isDisabled = disabled || loading;

  return (
    <form
      className="flex-1 flex flex-col justify-between border-1 border-gray-300 rounded-xs p-4 min-w-md"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="flex flex-col gap-4">
        <Input
          required
          disabled={isDisabled}
          label={t.property.name}
          type="text"
          value={values.name}
          onChange={(e) => onChange({ ...values, name: e.target.value })}
        />

        <div>
          <label className="block text-sm font-medium text-gray-500 mb-2">
            {t.property.parts}
          </label>
          {values.parts.length === 0 && (
            <p className="text-sm text-gray-400 mb-2">{t.property.partsEmpty}</p>
          )}
          <div className="flex flex-col gap-2">
            {values.parts.map((part, index) => (
              <div key={part.id ?? index} className="flex gap-2 items-end">
                <Input
                  required
                  className="flex-1"
                  disabled={isDisabled}
                  label={t.propertyPart.code}
                  type="text"
                  value={part.code}
                  onChange={(e) => {
                    const nextParts = [...values.parts];
                    nextParts[index] = { ...part, code: e.target.value };
                    onChange({ ...values, parts: nextParts });
                  }}
                />
                <Input
                  required
                  className="flex-1"
                  disabled={isDisabled}
                  label={t.propertyPart.name}
                  type="text"
                  value={part.name}
                  onChange={(e) => {
                    const nextParts = [...values.parts];
                    nextParts[index] = { ...part, name: e.target.value };
                    onChange({ ...values, parts: nextParts });
                  }}
                />
                <button
                  className="text-red-600 cursor-pointer disabled:opacity-50 p-2 mb-1"
                  disabled={isDisabled}
                  type="button"
                  onClick={() => {
                    onChange({
                      ...values,
                      parts: values.parts.filter((_, partIndex) => partIndex !== index),
                    });
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            ))}
          </div>
          <Button
            className="mt-2"
            disabled={isDisabled}
            type="button"
            onClick={() =>
              onChange({ ...values, parts: [...values.parts, createEmptyPart()] })
            }
          >
            <FontAwesomeIcon className="mr-2" icon={faPlus} />
            {t.property.addPart}
          </Button>
        </div>
      </div>

      {hasError && (
        <div className="flex items-center gap-2 text-red-600 mt-4">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <span>{t.property.error}</span>
        </div>
      )}

      <div className="flex justify-end gap-2 mt-4">
        <Button disabled={isDisabled} type="button" onClick={onCancel}>
          {t.cancel}
        </Button>
        <Button disabled={isDisabled || !isValid} loading={loading} type="submit">
          {t.send.submit}
        </Button>
      </div>
    </form>
  );
}
